import type { ReactNode } from 'react';
import type { FloatingPosition } from '@mantine/core';
import clsx from 'clsx';

import { FormLabel } from '../formlabel/FormLabel';
import { emptyValueFormatter } from '../table/valueFormatters';
import { TextWithTooltip } from '../tooltip/TextWithTooltip';

import inputStyles from '../input/input.module.scss';
import styles from './readOnlyInput.module.scss';

export type ReadOnlyInputProps = {
  description?: ReactNode;
  label?: string;
  showTitle?: boolean;
  tableLayout?: boolean;
  tooltipPosition?: FloatingPosition;
  value?: string | null;
  /** Shown instead of the empty-value marker when `value` is empty */
  placeholder?: string;
  hasError?: boolean;
};

export const ReadOnlyInput = ({
  description,
  label,
  showTitle = true,
  tableLayout,
  tooltipPosition,
  value,
  placeholder,
  hasError,
}: ReadOnlyInputProps) => {
  const isEmpty = value == null || value === '';
  const showPlaceholder = isEmpty && Boolean(placeholder);
  const displayValue = showPlaceholder
    ? placeholder
    : emptyValueFormatter(value);
  const valueClassName = clsx(
    hasError === true && styles['validation-error'],
    hasError === false && styles['validation-success'],
    showPlaceholder && styles['placeholder']
  );

  return (
    <div className={clsx({ [inputStyles['root--table-layout']]: tableLayout })}>
      {showTitle && (
        <FormLabel
          description={description}
          label={label}
          className={styles['label']}
        />
      )}
      <div
        className={clsx({
          [styles['value--table-layout']]: tableLayout,
        })}
      >
        <TextWithTooltip
          tooltipProps={{
            position: tooltipPosition,
          }}
          textProps={{
            lineClamp: tableLayout ? 2 : 1,
          }}
        >
          <span className={valueClassName}>{displayValue}</span>
        </TextWithTooltip>
      </div>
    </div>
  );
};
